import { useState, useEffect, useCallback } from 'react';
import { LIGHT, MUTE, FAINT, GHOST, GOLD, EMBER, BASE, SURFACE, DISPLAY, BODY, MONO } from '../lib/constants';
import { Horizon, Mo, Bd, Dp, Label, Ic } from './shared';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const CAT_COLORS = { psychology: EMBER, culture: '#a855f7', tips: GOLD, data: '#c0c0c0' };
const CAT_LABELS = { psychology: 'PSYCHOLOGY', culture: 'CULTURE & MONEY', tips: 'PRACTICAL TIP', data: 'BY THE NUMBERS' };
const CAT_ICONS = {
  psychology: () => <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke={EMBER} strokeWidth="1.6" strokeLinecap="round"><path d="M12 2a7 7 0 017 7c0 3-2 5-4 7v3H9v-3c-2-2-4-4-4-7a7 7 0 017-7zM9 19h6M10 22h4" /></svg>,
  culture: () => <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="#a855f7" strokeWidth="1.6" strokeLinecap="round"><circle cx="12" cy="12" r="10" /><path d="M2 12h20M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z" /></svg>,
  tips: () => Ic.check(16, GOLD),
  data: () => Ic.chart(16, '#c0c0c0'),
};

// Single card for Home screen
export function LiteracyCard({ card, onRefresh, onViewAll }) {
  if (!card) return null;
  const color = CAT_COLORS[card.category] || GOLD;
  const catIcon = CAT_ICONS[card.category]?.() || Ic.flame(16, GOLD);

  return (
    <div data-testid="literacy-card" style={{ padding: 16, background: 'var(--surface)', border: `1px solid ${FAINT}`, borderLeft: `3px solid ${color}`, position: 'relative' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        {catIcon}
        <Mo style={{ fontSize: 7, fontWeight: 700, color, letterSpacing: 2 }}>{CAT_LABELS[card.category] || card.category?.toUpperCase()}</Mo>
      </div>
      <Dp style={{ fontSize: 16, fontWeight: 800, color: 'var(--light)', display: 'block', marginBottom: 8, letterSpacing: -0.3 }}>{card.title}</Dp>
      <Bd style={{ fontSize: 12, color: 'var(--mute)', lineHeight: 1.7, display: 'block', marginBottom: 10 }}>{card.body}</Bd>
      {card.source && <Mo style={{ fontSize: 10, color: 'var(--ghost)', letterSpacing: 0.5 }}>— {card.source}</Mo>}
      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <button onClick={onRefresh} data-testid="literacy-refresh-btn" style={{ padding: '6px 10px', background: 'var(--faint)', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
          <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke={'var(--mute)'} strokeWidth="1.6" strokeLinecap="round"><polyline points="23 4 23 10 17 10" /><path d="M20.49 15a9 9 0 11-2.12-9.36L23 10" /></svg>
          <Mo style={{ fontSize: 10, fontWeight: 600, color: 'var(--mute)', letterSpacing: 0.5 }}>ANOTHER</Mo>
        </button>
        <button onClick={onViewAll} data-testid="literacy-view-all-btn" style={{ padding: '6px 10px', background: 'transparent', border: `1px solid ${GHOST}`, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
          <Mo style={{ fontSize: 10, fontWeight: 600, color: 'var(--mute)', letterSpacing: 0.5 }}>VIEW ALL</Mo>
        </button>
      </div>
    </div>
  );
}

// Full literacy library screen
export function LiteracyScreen() {
  const [cards, setCards] = useState([]);
  const [filter, setFilter] = useState('all');
  const [expanded, setExpanded] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/literacy/all`)
      .then(r => r.json())
      .then(data => { setCards(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const filtered = filter === 'all' ? cards : cards.filter(c => c.category === filter);
  const categories = ['all', 'psychology', 'culture', 'tips', 'data'];

  return (
    <div data-testid="literacy-screen" className="dettio-screen">
      <Horizon intensity={0.5} />
      <div className="dettio-screen__inner">
        <div className="dettio-screen__center" style={{ marginBottom: 20 }}>
          <Dp style={{ fontSize: 20, fontWeight: 800, color: 'var(--light)', display: 'block', letterSpacing: -0.5, marginBottom: 4 }}>Financial Literacy</Dp>
          <Bd style={{ fontSize: 12, color: 'var(--mute)', display: 'block' }}>Understanding why we spend changes how we spend</Bd>
        </div>

        {/* Category filter */}
        <div style={{ display: 'flex', gap: 4, marginBottom: 20, overflowX: 'auto', paddingBottom: 4 }}>
          {categories.map(c => (
            <button key={c} onClick={() => setFilter(c)} data-testid={`filter-${c}`} style={{
              padding: '8px 12px', whiteSpace: 'nowrap',
              background: filter === c ? (c === 'all' ? `${GOLD}15` : `${CAT_COLORS[c] || GOLD}15`) : FAINT,
              border: `1px solid ${filter === c ? (c === 'all' ? GOLD : CAT_COLORS[c] || GOLD) : 'transparent'}`,
              color: filter === c ? (c === 'all' ? GOLD : CAT_COLORS[c] || GOLD) : MUTE,
              fontFamily: MONO, fontSize: 10, fontWeight: 700, cursor: 'pointer', letterSpacing: 1
            }}>
              {c === 'all' ? 'ALL' : CAT_LABELS[c] || c.toUpperCase()}
            </button>
          ))}
        </div>

        <Mo style={{ fontSize: 9, color: 'var(--ghost)', display: 'block', marginBottom: 14 }}>{filtered.length} cards</Mo>

        {loading ? (
          <div className="pulse-a" style={{ textAlign: 'center', padding: 40 }}>
            <Mo style={{ fontSize: 12, color: 'var(--mute)', display: 'block' }}>Loading...</Mo>
          </div>
        ) : (
          filtered.map((card, idx) => {
            const color = CAT_COLORS[card.category] || GOLD;
            const isOpen = expanded[card.id];
            return (
              <div key={card.id} data-testid={`literacy-item-${card.id}`} className="fade-in" style={{
                marginBottom: 8, background: 'var(--surface)', border: `1px solid ${FAINT}`,
                borderLeft: `3px solid ${color}`, overflow: 'hidden',
                animationDelay: `${idx * 30}ms`
              }}>
                <button onClick={() => setExpanded(p => ({ ...p, [card.id]: !p[card.id] }))} style={{
                  width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '14px 14px',
                  background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left'
                }}>
                  <div style={{ flexShrink: 0 }}>{CAT_ICONS[card.category]?.() || Ic.flame(14, GOLD)}</div>
                  <div className="dettio-flex-1">
                    <Dp style={{ fontSize: 13, fontWeight: 800, color: 'var(--light)', display: 'block', letterSpacing: -0.2 }}>{card.title}</Dp>
                    <Mo style={{ fontSize: 7, color, letterSpacing: 1.5, marginTop: 2 }}>{CAT_LABELS[card.category]}</Mo>
                  </div>
                  <Mo style={{ fontSize: 14, color: 'var(--mute)', display: 'block' }}>{isOpen ? '−' : '+'}</Mo>
                </button>
                {isOpen && (
                  <div className="fade-in" style={{ padding: '0 14px 14px' }}>
                    <Bd style={{ fontSize: 12, color: 'var(--mute)', lineHeight: 1.7, display: 'block', marginBottom: 8 }}>{card.body}</Bd>
                    {card.source && <Mo style={{ fontSize: 10, color: 'var(--ghost)', letterSpacing: 0.5 }}>— {card.source}</Mo>}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
